<template>
  <div class="page-container">
    <div class="demand-hall-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- 页面头部 -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- 面包屑导航 -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- 头部Banner -->
      <SearchBanner 
        :title-main1="bannerTitleMain1"
        :title-main2="bannerTitleMain2"
        :subtitle="demandHallConfig.subtitle"
        :placeholder="demandHallConfig.searchPlaceholder"
        :button-text="demandHallConfig.searchButton || ''"
        :keyword.sync="searchKeyword"
        :background-image="localeImg('banner/gongxuzhongxin-xuqiudating.png')"
        search-object="demand"
        @search="handleSearch"
      />

      <!-- 筛选条件区 -->
      <div class="filter-section">
        <!-- 需求类别（一级，与成果超市共用 yAchievementTypev2） -->
        <div class="filter-row">
          <div class="filter-label">{{ demandHallConfig.filters?.categoryLabel || '' }}</div>
          <div class="filter-options">
            <div
              v-for="item in categoryMainOptions"
              :key="item.value"
              :class="['filter-option', { active: activeCategoryMain === item.value }]"
              @click="handleCategoryMainChange(item.value)"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- 需求子类（二级） -->
        <div v-if="visibleCategorySubOptions.length > 0" class="filter-row">
          <div class="filter-label">{{ demandHallConfig.filters?.subCategoryLabel || '需求子类：' }}</div>
          <div class="filter-options">
            <div
              v-for="item in visibleCategorySubOptions"
              :key="item.value"
              :class="['filter-option', { active: activeCategorySub === item.value }]"
              @click="activeCategorySub = item.value; handleFilterChange()"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- 所属领域 -->
        <div class="filter-row">
          <div class="filter-label">{{ demandHallConfig.filters?.fieldLabel || '' }}</div>
          <div class="filter-options">
            <div
              v-for="item in fieldOptions"
              :key="item.value"
              :class="['filter-option', { active: activeField === item.value }]"
              @click="activeField = item.value; handleFilterChange()"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- 合作方式 -->
        <div class="filter-row">
          <div class="filter-label">{{ demandHallConfig.filters?.cooperationLabel || '' }}</div>
          <div class="filter-options">
            <div
              v-for="item in cooperationOptions"
              :key="item.value"
              :class="['filter-option', { active: activeCooperation === item.value }]"
              @click="activeCooperation = item.value; handleFilterChange()"
            >
              {{ item.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- 需求列表 -->
      <div class="demand-list-container">
        <div v-if="loading" class="loading">{{ loadingText }}</div>
        <DemandList 
          v-else
          :items="demandList"
          :cooperation-type="config.demandSection?.itemMeta?.cooperationType || ''"
          :fee-label="config.demandSection?.itemMeta?.feeLabel || ''"
          :organization-label="config.demandSection?.itemMeta?.organizationLabel || ''"
          :location-label="config.demandSection?.itemMeta?.locationLabel || ''"
          :deadline-label="config.demandSection?.itemMeta?.deadlineLabel || ''"
          @item-click="handleItemClick"
        />

        <!-- 分页 -->
        <div class="pagination">
          <span class="pagination-info">{{ pageInfoText }}</span>
          <div class="pagination-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="goFirstPage">{{ paginationConfig?.firstPage || '' }}</button>
            <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">{{ paginationConfig?.prevPage || '' }}</button>
            <button
              v-for="page in visiblePages"
              :key="page"
              :class="['page-number', { active: currentPage === page }]"
              @click="handlePageChange(page)"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">{{ paginationConfig?.nextPage || '' }}</button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="goLastPage">{{ paginationConfig?.lastPage || '' }}</button>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getSupplyDemandConfig } from '@/config/supplyDemandConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import DemandList from '@/components/DemandList.vue'
import { getText, getCurrentLocale } from '@/utils/i18nText'
import { buildSubCategoryTabsFromDict, getVisibleSubCategoryTabs, CATEGORY_ALL_LABEL } from '@/utils/filterTabs'
import supplyDemandApi from '@/api/supplyDemand'

export default {
  name: 'DemandHall',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner,
    DemandList
  },
  data() {
    return {
      config: {},
      searchKeyword: '',
      activeCategoryMain: 'all',
      activeCategorySub: '',
      activeField: 'all',
      activeCooperation: 'all',
      currentPage: 1,
      pageSize: 16,
      totalCount: 0,
      loading: false,

      // 面包屑数据
      breadcrumbItems: [],

      // 筛选选项（从字典获取，类别与成果超市共用 yAchievementTypev2）
      categoryDictData: [],
      categoryMainOptions: [],
      categorySubOptions: [],
      fieldOptions: [],
      cooperationOptions: [],

      // 需求列表（由接口 /requirement-api/open/requirement/page-requirement-list 提供）
      demandList: [],
      sourceType: 'demand'
    }
  },
  watch: {
    currentPage() {
      this.loadDemandList()
    },
    '$route.query.type'(val) {
      this.sourceType = val === 'jiebang' ? 'jiebang' : 'demand'
      const breadcrumb = this.demandHallConfig.breadcrumb || {}
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: breadcrumb.center || '', path: '/supply-demand' },
        { label: this.sourceType === 'jiebang' ? '揭榜挂帅' : (breadcrumb.current || ''), path: '' }
      ]
      this.currentPage = 1
      this.loadDemandList()
    }
  },
  computed: {
    demandHallConfig() {
      return this.config.demandHall || {}
    },
    isJiebangType() {
      return this.sourceType === 'jiebang'
    },
    bannerTitleMain1() {
      return this.isJiebangType ? '揭榜' : (this.demandHallConfig.title1 || '')
    },
    bannerTitleMain2() {
      return this.isJiebangType ? '挂帅' : (this.demandHallConfig.title2 || '')
    },
    paginationConfig() {
      return this.demandHallConfig.pagination || {}
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize)
    },
    // 页码最多显示 6 个，采用滑动窗口
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const max = 6
      if (!total || total <= max) {
        return Array.from({ length: total || 1 }, (_, i) => i + 1)
      }
      let start = Math.max(1, current - 2)
      let end = start + max - 1
      if (end > total) {
        end = total
        start = end - max + 1
      }
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },
    pageInfoText() {
      const tpl = this.paginationConfig?.pageInfo || ''
      return tpl.replace('{total}', this.totalCount).replace('{pageSize}', this.pageSize)
    },
    loadingText() {
      return getText('common.loading')
    },
    negotiableText() {
      return getText('common.negotiable')
    },
    visibleCategorySubOptions() {
      return getVisibleSubCategoryTabs(this.categorySubOptions)
    }
  },
  created() {
    this.loadConfig()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    // 设置缩放比例
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$el && typeof this.$el.querySelector === 'function' ? this.$el.querySelector('.demand-hall-page') : null
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    
    // 加载配置
    async loadConfig() {
      this.config = getSupplyDemandConfig(getCurrentLocale())
      this.sourceType = this.$route.query?.type === 'jiebang' ? 'jiebang' : 'demand'
      const breadcrumb = this.demandHallConfig.breadcrumb || {}
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: breadcrumb.center || '', path: '/supply-demand' },
        { label: this.isJiebangType ? '揭榜挂帅' : (breadcrumb.current || ''), path: '' }
      ]
      
      // 加载字典数据
      await this.loadDictData()
      // 加载需求列表
      this.loadDemandList()
    },

    normalizeDictRows(data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.rows)) return data.rows
      if (data && typeof data === 'object') return Object.values(data).flat()
      return []
    },

    // 构建需求类别（一级 / 二级，与成果超市相同数据源 yAchievementTypev2）
    buildCategoryOptions(dictData) {
      const categoryDict = (dictData || []).filter(item => item && item.dictCode === 'yAchievementTypev2')
      this.categoryDictData = categoryDict

      const mainTypes = categoryDict
        .filter(item => item.parentCode === null || item.parentCode === '' || item.parentCode === undefined)
        .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))

      this.categoryMainOptions = [
        { label: CATEGORY_ALL_LABEL, value: 'all' },
        ...mainTypes.map(item => ({
          label: item.itemValue || '',
          value: item.itemCode || ''
        }))
      ]

      this.updateCategorySubOptions(this.activeCategoryMain)
    },

    updateCategorySubOptions(mainValue) {
      if (!mainValue || mainValue === 'all') {
        this.categorySubOptions = []
        this.activeCategorySub = ''
        return
      }

      const subTypes = this.categoryDictData.filter(
        item => String(item.parentCode || '') === String(mainValue)
      )
      this.categorySubOptions = buildSubCategoryTabsFromDict(subTypes, {
        allLabel: CATEGORY_ALL_LABEL,
        allValue: ''
      })
      this.activeCategorySub = this.categorySubOptions[0]?.value ?? ''
    },

    handleCategoryMainChange(value) {
      this.activeCategoryMain = value
      this.updateCategorySubOptions(value)
      this.handleFilterChange()
    },

    getCategoryQueryParams() {
      const params = {}
      if (this.activeCategoryMain && this.activeCategoryMain !== 'all') {
        params.firstCategory = this.activeCategoryMain
      }
      if (this.activeCategorySub) {
        params.secondCategory = this.activeCategorySub
      }
      return params
    },

    // 加载字典数据（类别 / 领域 / 合作方式）
    async loadDictData() {
      try {
        const dictCodeList = ['yAchievementTypev2', 'yField', 'yMatcherCoopType']
        const response = await supplyDemandApi.getDictData(dictCodeList)
        const result = response.data || {}

        if (result.status === 0) {
          const dictData = this.normalizeDictRows(result.data)

          this.buildCategoryOptions(dictData)

          // 处理领域
          const fieldDict = dictData
            .filter(item => item && item.dictCode === 'yField')
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))

          this.fieldOptions = [
            { label: CATEGORY_ALL_LABEL, value: 'all' },
            ...fieldDict.map(item => ({
              label: item.itemValue || '',
              value: item.itemCode || ''
            }))
          ]

          // 合作方式：从字典 yMatcherCoopType 读取
          const coopDict = dictData
            .filter(item => item && item.dictCode === 'yMatcherCoopType')
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))

          this.cooperationOptions =
            coopDict.length > 0
              ? [
                { label: CATEGORY_ALL_LABEL, value: 'all' },
                ...coopDict.map(item => ({
                  label: item.itemValue || '',
                  value: item.itemCode || ''
                }))
              ]
              : (this.demandHallConfig.cooperationOptions || [
                { label: CATEGORY_ALL_LABEL, value: 'all' }
              ])

          this.activeCategoryMain = this.categoryMainOptions[0]?.value || 'all'
          this.updateCategorySubOptions(this.activeCategoryMain)
          this.activeField = this.fieldOptions[0]?.value || 'all'
          this.activeCooperation = this.cooperationOptions[0]?.value || 'all'
        } else {
          this.fieldOptions = this.demandHallConfig.fieldOptions || []
          this.cooperationOptions = this.demandHallConfig.cooperationOptions || []
        }
      } catch (error) {
        console.error('加载字典数据失败:', error)
        this.fieldOptions = this.demandHallConfig.fieldOptions || []
        this.cooperationOptions = this.demandHallConfig.cooperationOptions || []
      }
    },

    // 加载需求列表
    async loadDemandList() {
      this.loading = true
      try {
        const params = {
          ...this.getCategoryQueryParams(),
          field: this.activeField === 'all' ? '' : this.activeField,
          cooperationMethod: this.activeCooperation === 'all' ? '' : this.activeCooperation,
          // 分页必填参数
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          // 模糊搜索：需求名称
          requirementName: this.searchKeyword || '',
          // 揭榜挂帅仍走原列表接口，仅附加 recruit 参数
          recruit: this.isJiebangType ? true : undefined
        }

        const response = await supplyDemandApi.getRequirementPageList(params)
        const result = response.data || {}
        if (result.status === 0 && result.data) {
          const list = (result.data.rows && Array.isArray(result.data.rows)) ? result.data.rows : []

          this.demandList = list.map(item => {
            // 费用显示
            let priceText = this.negotiableText
            const feeType = String(item.transferFeeType ?? '')
            if (feeType === '1') {
              priceText = item.transferFeeTypeLabel || ''
            } else if (item.transferFeeMin != null && item.transferFeeMax != null) {
              priceText = `${item.transferFeeMin}-${item.transferFeeMax}万元`
            } else if (item.transferFeeTypeLabel) {
              priceText = item.transferFeeTypeLabel
            }

            // 位置
            const location = [item.provinceLabel, item.cityLabel, item.districtLabel].filter(Boolean).join('')

            return {
              id: item.id,
              title: item.requirementName || '',
              price: priceText,
              organization: item.unitPublic ? (item.unitName || '') : '',
              location,
              deadline: item.deadline || '',
              tag: item.firstCategoryLabel || item.requirementType || '',
              // 合作方式：接口返回的 cooperationMethodLabel
              cooperationType: item.cooperationMethodLabel || ''
            }
          })

          // 更新总数
          this.totalCount = result.data.totalCount || 0
        } else {
          this.demandList = []
          this.totalCount = 0
        }
      } catch (error) {
        console.error('加载需求列表失败:', error)
        this.demandList = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      console.log('搜索关键词:', this.searchKeyword)
      this.resetPageAndReload()
    },

    handleModuleClick(module) {
      if (module && module.id === 'home') {
        this.$router.push('/index')
      }
    },

    // 筛选条件变化
    handleFilterChange() {
      this.resetPageAndReload()
    },

    resetPageAndReload() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }
      this.loadDemandList()
    },
    
    // 点击需求卡片
    handleItemClick(item) {
      console.log('点击需求:', item.title)
      // 揭榜挂帅仍走旧版需求详情
      const path = this.sourceType === 'jiebang'
        ? '/supply-demand/demand-detail-legacy'
        : '/supply-demand/demand-detail'
      this.$router.push({
        path,
        query: { 
          id: item.id,
          type: this.sourceType
        }
      })
    },
    
    // 页码变化
    handlePageChange(page) {
      this.currentPage = page
      // watch 会自动触发 loadDemandList
    },
    
    // 上一页
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    
    // 下一页
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    goFirstPage() {
      if (this.currentPage === 1) return
      this.currentPage = 1
    },

    goLastPage() {
      if (this.currentPage === this.totalPages) return
      this.currentPage = this.totalPages
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.demand-hall-page {
  width: 1920px;
  min-height: 100vh;
  background: linear-gradient(180deg, #f5f6f8 0%, #ececeb 40%, #f9f9f9 100%);
  transform-origin: top left;
}

// 筛选条件区
.filter-section {
  width: 1700px;
  margin: 20px auto;
  margin-left: 110px;
  background: #FFFFFF;
  border-radius: 4px;
  padding: 20px 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.filter-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .filter-label {
    font-family: Source Han Sans CN;
    font-size: 14px;
    color: #666666;
    min-width: 80px;
    flex-shrink: 0;
    padding-top: 8px;
  }
  
  .filter-options {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .filter-option {
    padding: 6px 16px;
    background: #F8F8F8;
    border: 1px solid transparent;
    border-radius: 4px;
    font-family: Source Han Sans CN;
    font-size: 14px;
    color: #666666;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      background: #FFF9E6;
      border-color: rgba(160, 144, 60, 0.3);
      color: #A0903C;
    }
    
    &.active {
      background: #A0903C;
      border-color: #A0903C;
      color: #FFFFFF;
      font-weight: 500;
    }
  }
}

// 需求列表容器
.demand-list-container {
  width: 1700px;
  margin: 0 auto 40px;
  margin-left: 110px;
  
  .loading {
    text-align: center;
    padding: 40px;
    font-family: Source Han Sans CN;
    font-size: 14px;
    color: #666666;
  }
}

// 分页
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  margin-top: 30px;

  .pagination-info {
    font-family: Source Han Sans CN;
    font-size: 13px;
    color: #666666;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .page-btn,
    .page-number {
      min-width: 36px;
      height: 32px;
      padding: 0 12px;
      background: #ffffff;
      border: 1px solid #dddddd;
      border-radius: 2px;
      font-family: Source Han Sans CN;
      font-size: 13px;
      color: #666666;
      cursor: pointer;
      transition: all 0.3s;

      &:hover:not(:disabled):not(.active) {
        border-color: #a0903c;
        color: #a0903c;
      }

      &.active {
        background: #a0903c;
        border-color: #a0903c;
        color: #ffffff;
      }

      &:disabled {
        background: #f5f5f5;
        border-color: #dddddd;
        color: #cccccc;
        cursor: not-allowed;
      }
    }
  }
}


@import '@/assets/scss/mixins/portal-filter-locale-zh-cn.scss';

.demand-hall-page.locale-zh-CN {
  @include portal-filter-locale-zh-cn;
}
</style>
